import { z } from 'zod';

import { coachGradeSchema } from '../coach-grades.js';
import { CSVUploadResponse } from '../csv.js';
import {
  coachAttendanceGradeSchema,
  coachSchema,
  coachStatusSchema,
  fullCoachSchema,
  updateCoachOptionsSchema,
} from './coach.js';
import {
  filteringSchemaFrom,
  pagingSchema,
  sortOrderSchemaFrom,
  sortingSchemaFrom,
  typeaheadFilterSetSchema,
} from './modifier.js';
import { studentSchema, studentWithScheduleInfoSchema, updateStudentOptionsSchema } from './student.js';
import { roleSchema, userRoleAssignmentSchema, userRoleWithSchoolSchema } from './user-role.js';
import { paginatedResponse } from './util-schema-types.js';

export const ssoExternalKeysSchema = z.object({
  iss: z.string(),
  sub: z.string(),
});
export type SSOExternalKeys = z.infer<typeof ssoExternalKeysSchema>;

export const userLikeSchema = z.object({
  type: z.literal('user'),
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullNameOrEmail: z.string(),
  displayName: z.string().nullable().optional(),
  coachName: z.string().optional(),
  shortCode: z.string().nullable().optional(),
});
/**
 * Represents any entity with a UUID, a first name, and a last name.
 */
export type UserLike = z.infer<typeof userLikeSchema>;

export const userSchema = userLikeSchema.extend({
  email: z.string(),
  timeZone: z.string().nullable(),
  phone: z.string().nullable(),

  externalKeys: ssoExternalKeysSchema.nullable(),
  isRegistrable: z.boolean(),
  shortCode: z.string().nullable(),
});
export type User = z.infer<typeof userSchema>;

export const loggedInUserSchema = userSchema.extend({
  userRoles: z.array(userRoleWithSchoolSchema),

  // impersonatingAs is technically a loggedInUserSchema but manually defining it here
  // to avoid dealing with zod recursion types, and force impersonatingAs to be null. i.e.
  // impersonated user cannot impersonate another user
  impersonatingAs: userSchema
    .extend({
      userRoles: z.array(userRoleWithSchoolSchema),
      impersonatingAs: z.null(),
      grade: z.string().nullable(),
      coachStatus: coachStatusSchema.nullable(),
    })
    .nullable(),
  grade: z.string().nullable(),
  coachStatus: coachStatusSchema.nullable(),
});
export type LoggedInUser = z.infer<typeof loggedInUserSchema>;

export const userWithRolesAndProfilesSchema = userSchema.extend({
  coachProfile: z.lazy(() => coachSchema).nullable(),
  studentProfile: studentSchema.nullable(),
  userRoles: z.array(userRoleWithSchoolSchema),
});
export type UserWithRolesAndProfiles = z.infer<typeof userWithRolesAndProfilesSchema>;

export const userWithRolesProfilesSchedulesSchema = userWithRolesAndProfilesSchema.extend({
  studentProfile: studentWithScheduleInfoSchema.nullable(),
});
export type UserWithRolesProfilesSchedules = z.infer<typeof userWithRolesProfilesSchedulesSchema>;

export const singleUserFullSchema = userWithRolesProfilesSchedulesSchema.extend({
  edlinkId: z.string().nullable(),
});
export type SingleUserFull = z.infer<typeof singleUserFullSchema>;

export const userWithCoachProfileSchema = userSchema.extend({
  coachProfile: z.lazy(() => coachSchema),
});
/**
 * Some queries on coach profiles include joins to the user table. In these
 * cases, instead of representing them as coach objects with a user, we
 * normalize them into user objects that only contain a coach profile.
 */
export type UserWithCoachProfile = z.infer<typeof userWithCoachProfileSchema>;

//#region getUsers

export const getUsersSortableColumnSchema = userSchema.keyof().extract(['email', 'fullNameOrEmail', 'timeZone']);
export type GetUsersSortableColumn = z.infer<typeof getUsersSortableColumnSchema>;

const getUsersSortingSchema = sortingSchemaFrom(getUsersSortableColumnSchema);

export const getUsersSortOrderSchema = sortOrderSchemaFrom(getUsersSortableColumnSchema);
export type GetUsersSortOrder = z.infer<typeof getUsersSortOrderSchema>;

export const getUserWithCoachProfileSortableColumnSchema = z.union([
  getUsersSortableColumnSchema,
  fullCoachSchema.keyof().extract(['status', 'qualityGrade', 'needsReview', 'studentGroupCount']),
]);
export type GetUserWithCoachProfileSortableColumn = z.infer<typeof getUserWithCoachProfileSortableColumnSchema>;

export const getUsersFilterSchema = z.object({
  query: z.string().optional(),
  roles: z.array(roleSchema).optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  teachingGrades: z.array(coachGradeSchema).optional(),
  attendanceGrades: z.array(coachAttendanceGradeSchema).optional(),
});
export type GetUsersFilter = z.infer<typeof getUsersFilterSchema>;

export const getUsersModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getUsersFilterSchema))
  .merge(getUsersSortingSchema);
export type GetUsersModifier = z.infer<typeof getUsersModifierSchema>;

export const getUsersResultsSchema = paginatedResponse.extend({
  users: z.array(userWithRolesAndProfilesSchema),
});
export type GetUsersResults = z.infer<typeof getUsersResultsSchema>;

//#endregion

//#region getUsersTypeahead

export const getUsersTypeaheadFilterSchema = typeaheadFilterSetSchema.extend({
  roles: z.array(roleSchema).optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
});
export type GetUsersTypeaheadFilter = z.infer<typeof getUsersTypeaheadFilterSchema>;

export const getUsersTypeaheadModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getUsersTypeaheadFilterSchema))
  .merge(getUsersSortingSchema);
export type GetUsersTypeaheadModifier = z.infer<typeof getUsersTypeaheadModifierSchema>;

export const getUsersTypeaheadResultsSchema = z.array(loggedInUserSchema);
export type GetUsersTypeaheadResults = z.infer<typeof getUsersTypeaheadResultsSchema>;

//#endregion

export const userRoleAssignmentOptionsSchema = z.array(userRoleAssignmentSchema);
export type UserRoleAssignmentOptions = z.infer<typeof userRoleAssignmentOptionsSchema>;

export const commonUserOptionsSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  timeZone: z.string().nullable(),
  edlinkId: z.string().optional().nullable(),

  externalKeys: ssoExternalKeysSchema.nullable(),
  isRegistrable: z.boolean(),
});

export const createUserOptionsSchema = commonUserOptionsSchema.extend({
  assignRoles: z.array(userRoleAssignmentSchema).optional(),
  coachProfile: z.lazy(() => updateCoachOptionsSchema).optional(),
  studentProfile: z.lazy(() => updateStudentOptionsSchema).optional(),
});
export type CreateUserOptions = z.infer<typeof createUserOptionsSchema>;

export const updateUserOptionsSchema = createUserOptionsSchema.partial();
export type UpdateUserOptions = z.infer<typeof updateUserOptionsSchema>;

export type CreateStudentUsersCsvResult = CSVUploadResponse;

export type CreateCoachUsersCsvResult = CSVUploadResponse;
